import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Mail,
  Users,
  Download,
  Search,
  ArrowLeft,
  CreditCard,
  UserPlus,
  Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEmails() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, users, subscribers, paid
  const [stats, setStats] = useState({ total: 0, users_count: 0, subscribers_count: 0 });

  useEffect(() => {
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${adminToken}`;
    }
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/admin/emails?limit=500`);
      setEmails(response.data.emails || []);
      setStats({
        total: response.data.total,
        users_count: response.data.users_count,
        subscribers_count: response.data.subscribers_count
      });
    } catch (error) {
      toast.error("Failed to load emails");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const adminToken = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/emails/export`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "coach_coop_emails.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Emails exported!");
    } catch (error) {
      toast.error("Failed to export emails");
    }
  };

  const filteredEmails = emails.filter(email => {
    const matchesSearch = 
      email.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    switch (filter) {
      case "users":
        return email.type === "user";
      case "subscribers":
        return email.type === "subscriber";
      case "paid":
        return email.has_payment;
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" /> Admin
              </Button>
            </Link>
            <h1 className="text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" />
              Email List
            </h1>
          </div>
          <Button onClick={handleExport} className="bg-primary hover:bg-primary/90">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-primary">{stats.total}</div>
              <div className="text-xs text-muted-foreground uppercase">Total Emails</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-green-500">{stats.users_count}</div>
              <div className="text-xs text-muted-foreground uppercase">Registered Users</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-black text-blue-500">{stats.subscribers_count}</div>
              <div className="text-xs text-muted-foreground uppercase">Subscribers Only</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-card border-border mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search emails or names..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-secondary border-border"
                />
              </div>
              <div className="flex gap-2">
                {[
                  { value: "all", label: "All", icon: Mail },
                  { value: "users", label: "Users", icon: Users },
                  { value: "subscribers", label: "Subscribers", icon: UserPlus },
                  { value: "paid", label: "Paid", icon: CreditCard }
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    variant={filter === value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilter(value)}
                    className={filter === value ? "bg-primary" : ""}
                  >
                    <Icon className="h-4 w-4 mr-1" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
              {filteredEmails.length} emails found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredEmails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No emails found
                </div>
              ) : (
                filteredEmails.map((email, i) => (
                  <motion.div
                    key={email.email + i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm hover:bg-secondary/80 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        email.type === "user" 
                          ? email.has_payment 
                            ? "bg-green-500/20" 
                            : "bg-yellow-500/20"
                          : "bg-blue-500/20"
                      }`}>
                        {email.type === "user" ? (
                          email.has_payment ? (
                            <CreditCard className="h-4 w-4 text-green-500" />
                          ) : (
                            <Users className="h-4 w-4 text-yellow-500" />
                          )
                        ) : (
                          <UserPlus className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{email.email}</div>
                        <div className="text-xs text-muted-foreground">
                          {email.name && <span>{email.name} • </span>}
                          {email.type === "user" ? (
                            <span className={email.has_payment ? "text-green-500" : "text-yellow-500"}>
                              {email.subscription || "no plan"} {email.has_payment && "✓ paid"}
                            </span>
                          ) : (
                            <span className="text-blue-500">subscriber</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {email.date ? new Date(email.date).toLocaleDateString() : ""}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
